import { notification } from "antd";
import { GlobalStoreContextType } from "../../context/GlobalStoreContext";
import { Area } from "../../models/Area";
import { Dropoff } from "../../models/Dropoff";
import { Equipment } from "../../models/Equipment";
import { Gate } from "../../models/Gate";
import { GateToDropoffRoute } from "../../models/GateToDropoffRoute";
import { HaulRoute } from "../../models/HaulRoute";
import { MapObject } from "../../models/MapObject";
import { geometry } from "../../models/MapObject.model";
import { MapObjectRadius } from "../../models/MapObjectRadius";
import { WaypointDirection } from "../../models/WaypointDirection";
import PlotAPI from "../../plotAPI";
import {
    createWaypointDirection,
    destroyWaypointDirection,
    updateWaypointDirection,
} from "../PlotAPI/WaypointDirection";

const handleSaveWaypointDirection = async (
    mappable: Area | Dropoff | Equipment | Gate | HaulRoute | GateToDropoffRoute,
    project: any
) => {
    if (
        !mappable ||
        !(mappable instanceof Area || mappable instanceof HaulRoute || mappable instanceof GateToDropoffRoute)
    ) {
        return Promise.resolve();
    }
    const projectToken = project.unique_token;
    const teamSubdomain = project.claimed_by_team_subdomain || "projects";

    if (!mappable.waypointDirection?.id && mappable.waypointDirection?._destroy) {
        mappable.waypointDirection = undefined;
    }

    if (!mappable.waypointDirection) {
        return Promise.resolve();
    }

    const tempWaypoint = Object.create(mappable.waypointDirection) as WaypointDirection;
    tempWaypoint.waypointableId = mappable.id;
    tempWaypoint.waypointableType =
        mappable instanceof Area ? "Area" : mappable instanceof HaulRoute ? "HaulRoute" : "GateToDropoffRoute";

    if (tempWaypoint._destroy) {
        return destroyWaypointDirection(tempWaypoint, teamSubdomain, projectToken);
    }
    if (!tempWaypoint.id) {
        return createWaypointDirection(tempWaypoint, teamSubdomain, projectToken);
    }
    return updateWaypointDirection(tempWaypoint, teamSubdomain, projectToken);
};

const handleUpdateMappable = async (
    mappble: Area | Dropoff | Equipment | Gate | HaulRoute | GateToDropoffRoute,
    context: GlobalStoreContextType,
    taggedTeams: number[]
) => {
    if (mappble instanceof Area) {
        return PlotAPI.updateArea(
            context.currentProjectInfo.claimed_by_team_subdomain || "projects",
            context.currentProjectInfo.unique_token,
            mappble,
            taggedTeams,
            context.currentProjectInfo.timezone
        ).catch((error) => {
            notification.error({
                message: "Unable to update Area",
                description: error.message || "",
            });
        });
    }
    if (mappble instanceof Dropoff) {
        return PlotAPI.updateDropoff(
            context.currentProjectInfo.claimed_by_team_subdomain || "projects",
            context.currentProjectInfo.unique_token,
            mappble,
            taggedTeams,
            context.currentProjectInfo.timezone
        ).then((data) => {
            if (!data) {
                notification.error({
                    message: "Unable to update Dropoff",
                    description: "",
                });
            }
        });
    }
    if (mappble instanceof Equipment) {
        return PlotAPI.updateEquipment(
            context.currentProjectInfo.claimed_by_team_subdomain || "projects",
            context.currentProjectInfo.unique_token,
            mappble,
            taggedTeams,
            context.currentProjectInfo.timezone
        ).then((data) => {
            if (!data) {
                notification.error({
                    message: "Unable to update Equipment",
                    description: "",
                });
            }
        });
    }
    if (mappble instanceof Gate) {
        return PlotAPI.updateGate(
            context.currentProjectInfo.claimed_by_team_subdomain || "projects",
            context.currentProjectInfo.unique_token,
            mappble,
            context.currentProjectInfo.timezone
        ).then((data) => {
            if (!data) {
                notification.error({
                    message: "Unable to update Gate",
                    description: "",
                });
            }
        });
    }
    if (mappble instanceof HaulRoute) {
        return PlotAPI.updateHaulRoute(
            context.currentProjectInfo.claimed_by_team_subdomain || "projects",
            context.currentProjectInfo.unique_token,
            mappble,
            context.currentProjectInfo.timezone
        ).then((data) => {
            if (!data) {
                notification.error({
                    message: "Unable to update Haul Route",
                    description: "",
                });
            }
        });
    }
    if (mappble instanceof GateToDropoffRoute) {
        return PlotAPI.updateGateToDropoffRoute(
            context.currentProjectInfo.claimed_by_team_subdomain || "projects",
            context.currentProjectInfo.unique_token,
            mappble,
            context.currentProjectInfo.timezone
        ).then((data) => {
            if (!data) {
                notification.error({
                    message: "Unable to update Gate to Dropoff Route",
                    description: "",
                });
            }
        });
    }
    return;
};

const handleUpdateMapObject = async (newMapObject: MapObject, context: GlobalStoreContextType) => {
    const newCurrentMapObject = Object.create(newMapObject) as MapObject;
    const originalMapObject = context
        .mapObjectsForProject(context.currentProjectInfo.unique_token)
        .find((object) => object.id === newCurrentMapObject.id);

    if (!originalMapObject) {
        notification.error({
            message: "Unable to find Map Object",
            description: "The Map Object you are trying to update does not exist.",
        });
        return;
    }

    for (const oldRadius of originalMapObject.radiuses || []) {
        if (!newCurrentMapObject.radiuses?.find((e) => e.id === oldRadius.id)) {
            if (!newCurrentMapObject.radiuses) {
                newCurrentMapObject.radiuses = [];
            }

            const newRadius = MapObjectRadius.fromApiObject(
                {
                    id: oldRadius.id,
                },
                context.currentProjectInfo.timezone
            );

            newRadius._destroy = true; // Mark this for destruction

            newCurrentMapObject.radiuses.push(newRadius);
        }
    }
    return PlotAPI.updateMapObject(
        context.currentProjectInfo.claimed_by_team_subdomain || "projects",
        context.currentProjectInfo.unique_token,
        newCurrentMapObject,
        context.currentProjectInfo.timezone
    )
        .then((res) => {
            if (!res) {
                throw new Error("Unable to update Map Object");
            }
            return res;
        })
        .catch((error) => {
            notification.error({
                message: "Unable to update Map Object",
                description: "",
            });

            return;
        });
};

const coordinatesEqual = (a: [number, number], b: [number, number]): boolean => a[0] === b[0] && a[1] === b[1];

// A GateToDropoffRoute starts at its gate's position (see Helper.onDrawCreate), but the gate can be moved later on its
// own edit panel, so re-sync every attached route's start point whenever a Gate is saved rather than letting them
// silently drift apart. Routes are drawn in one fixed color (see Helper.GATE_TO_DROPOFF_ROUTE_COLOR), so only the
// position needs syncing.
const syncGateToDropoffRoutes = async (gate: Gate, gateMapObject: MapObject, context: GlobalStoreContextType) => {
    const newStart = gateMapObject.getCenter() as [number, number];

    const routeMapObjectsToUpdate = context
        .gateToDropoffRoutesForProject(context.currentProjectInfo.unique_token)
        .filter((route) => !route.archived && route.gateId === gate.id)
        .map((route) => route.getMapObject(context))
        .filter((routeMapObject): routeMapObject is MapObject => !!routeMapObject)
        .map((routeMapObject) => {
            if (
                !(routeMapObject.coordinates?.length > 0) ||
                coordinatesEqual(routeMapObject.coordinates[0], newStart)
            ) {
                return null;
            }

            const newRouteMapObject = Object.create(routeMapObject) as MapObject;
            newRouteMapObject.coordinates = [newStart, ...routeMapObject.coordinates.slice(1)];
            return newRouteMapObject;
        })
        .filter((routeMapObject): routeMapObject is MapObject => !!routeMapObject);

    await Promise.all(routeMapObjectsToUpdate.map((routeMapObject) => handleUpdateMapObject(routeMapObject, context)));
};

const routesForDropoff = (dropoff: Dropoff, context: GlobalStoreContextType): GateToDropoffRoute[] => {
    return context
        .gateToDropoffRoutesForProject(context.currentProjectInfo.unique_token)
        .filter((route) => !route.archived && route.dropoffId === dropoff.id);
};

// Mirrors syncGateToDropoffRoutes above, but re-syncs a GateToDropoffRoute's end point (rather than its start) from its
// dropoff whenever that dropoff is saved. A polygon dropoff's endpoint is its centroid (see MapObject#getCenter) rather
// than a raw coordinate, since a polygon has no single "position".
const syncDropoffToRoutes = async (dropoff: Dropoff, dropoffMapObject: MapObject, context: GlobalStoreContextType) => {
    const newEnd = dropoffMapObject.getCenter() as [number, number];

    const routeMapObjectsToUpdate = routesForDropoff(dropoff, context)
        .map((route) => route.getMapObject(context))
        .filter((routeMapObject): routeMapObject is MapObject => !!routeMapObject)
        .map((routeMapObject) => {
            const lastIndex = (routeMapObject.coordinates?.length || 0) - 1;
            if (lastIndex < 0 || coordinatesEqual(routeMapObject.coordinates[lastIndex], newEnd)) {
                return null;
            }

            const newRouteMapObject = Object.create(routeMapObject) as MapObject;
            newRouteMapObject.coordinates = [...routeMapObject.coordinates.slice(0, -1), newEnd];
            return newRouteMapObject;
        })
        .filter((routeMapObject): routeMapObject is MapObject => !!routeMapObject);

    await Promise.all(routeMapObjectsToUpdate.map((routeMapObject) => handleUpdateMapObject(routeMapObject, context)));
};

export const updateMappable = async (
    currentMappable: Area | Dropoff | Equipment | Gate | HaulRoute | GateToDropoffRoute,
    currentMapObject: MapObject,
    context: GlobalStoreContextType,
    taggedTeams: number[]
) => {
    let newCurrentMappable: Area | Dropoff | Equipment | Gate | HaulRoute | GateToDropoffRoute =
        currentMappable instanceof Area
            ? (Object.create(currentMappable) as Area)
            : currentMappable instanceof Dropoff
              ? (Object.create(currentMappable) as Dropoff)
              : currentMappable instanceof Equipment
                ? (Object.create(currentMappable) as Equipment)
                : currentMappable instanceof Gate
                  ? (Object.create(currentMappable) as Gate)
                  : currentMappable instanceof HaulRoute
                    ? (Object.create(currentMappable) as HaulRoute)
                    : (Object.create(currentMappable) as GateToDropoffRoute);

    const originalMappable =
        currentMappable instanceof Area
            ? context
                  .mapAreasForProject(context.currentProjectInfo.unique_token)
                  .find((area) => area.id === newCurrentMappable.id)
            : currentMappable instanceof Dropoff
              ? context
                    .dropoffsForProject(context.currentProjectInfo.unique_token)
                    .find((dropoff) => dropoff.id === newCurrentMappable.id)
              : currentMappable instanceof Equipment
                ? context
                      .equipmentForProject(context.currentProjectInfo.unique_token)
                      .find((equipment) => equipment.id === newCurrentMappable.id)
                : currentMappable instanceof Gate
                  ? context
                        .gatesForProject(context.currentProjectInfo.unique_token)
                        .find((gate) => gate.id === newCurrentMappable.id)
                  : currentMappable instanceof HaulRoute
                    ? context
                          .haulRoutesForProject(context.currentProjectInfo.unique_token)
                          .find((haulRoute) => haulRoute.id === newCurrentMappable.id)
                    : context
                          .gateToDropoffRoutesForProject(context.currentProjectInfo.unique_token)
                          .find((route) => route.id === newCurrentMappable.id);

    if (!originalMappable) {
        const typeName =
            currentMappable instanceof Area
                ? "Area"
                : currentMappable instanceof Dropoff
                  ? "Dropoff"
                  : currentMappable instanceof Equipment
                    ? "Equipment"
                    : currentMappable instanceof Gate
                      ? "Gate"
                      : currentMappable instanceof HaulRoute
                        ? "Haul Route"
                        : "Gate to Dropoff Route";
        notification.error({
            message: `Unable to find ${typeName}`,
            description: `The ${typeName} you are trying to update does not exist.`,
        });
        return;
    }

    return Promise.all([
        handleUpdateMappable(newCurrentMappable, context, taggedTeams),
        handleUpdateMapObject(currentMapObject, context),
        handleSaveWaypointDirection(newCurrentMappable, context.currentProjectInfo),
        newCurrentMappable instanceof Gate
            ? syncGateToDropoffRoutes(newCurrentMappable, currentMapObject, context)
            : Promise.resolve(),
        newCurrentMappable instanceof Dropoff
            ? syncDropoffToRoutes(newCurrentMappable, currentMapObject, context)
            : Promise.resolve(),
    ]);
};

export const createMappable = async (
    currentMappable: Area | Dropoff | Equipment | Gate | HaulRoute | GateToDropoffRoute,
    currentMapObject: MapObject,
    context: GlobalStoreContextType,
    taggedTeams: number[]
): Promise<Area | Dropoff | Equipment | Gate | HaulRoute | GateToDropoffRoute | null> => {
    if (currentMappable instanceof Area) {
        return PlotAPI.createArea(
            context.currentProjectInfo.claimed_by_team_subdomain || "projects",
            context.currentProjectInfo.unique_token,
            currentMappable,
            currentMapObject,
            taggedTeams,
            context.currentProjectInfo.timezone
        )
            .then((res) => {
                if (!res) {
                    throw new Error("Unable to create Area");
                }

                return res as Area;
            })
            .catch((error) => {
                notification.error({
                    message: "Unable to create Area",
                    description: error.message || "",
                });

                return null;
            });
    }
    if (currentMappable instanceof Dropoff) {
        return PlotAPI.createDropoff(
            context.currentProjectInfo.claimed_by_team_subdomain || "projects",
            context.currentProjectInfo.unique_token,
            currentMappable,
            currentMapObject,
            taggedTeams,
            context.currentProjectInfo.timezone
        )
            .then((res) => {
                if (!res) {
                    throw new Error("Unable to create Dropoff");
                }

                return res as Dropoff;
            })
            .catch((error) => {
                notification.error({
                    message: "Unable to create Dropoff",
                    description: error.message || "",
                });

                return null;
            });
    }
    if (currentMappable instanceof Equipment) {
        return PlotAPI.createEquipment(
            context.currentProjectInfo.claimed_by_team_subdomain || "projects",
            context.currentProjectInfo.unique_token,
            currentMappable,
            currentMapObject,
            taggedTeams,
            context.currentProjectInfo.timezone
        )
            .then((data) => {
                if (!data) {
                    throw new Error("Unable to create Equipment");
                }
                return data;
            })
            .catch((error) => {
                notification.error({
                    message: "Unable to create Equipment",
                    description: error.message || "",
                });

                return null;
            });
    }
    if (currentMappable instanceof Gate) {
        return PlotAPI.createGate(
            context.currentProjectInfo.claimed_by_team_subdomain || "projects",
            context.currentProjectInfo.unique_token,
            currentMappable,
            currentMapObject,
            context.currentProjectInfo.timezone
        )
            .then((data) => {
                if (!data) {
                    throw new Error("Unable to create Gate");
                }
                return data;
            })
            .catch((error) => {
                notification.error({
                    message: "Unable to create Gate",
                    description: error.message || "",
                });

                return null;
            });
    }
    if (currentMappable instanceof HaulRoute) {
        return PlotAPI.createHaulRoute(
            context.currentProjectInfo.claimed_by_team_subdomain || "projects",
            context.currentProjectInfo.unique_token,
            currentMappable,
            currentMapObject,
            context.currentProjectInfo.timezone
        )
            .then((res) => {
                if (!res) {
                    throw new Error("Unable to create Haul Route");
                }

                const haulRoute = res as HaulRoute;

                if (haulRoute && currentMappable.waypointDirection) {
                    const tempWaypoint = Object.create(currentMappable.waypointDirection) as WaypointDirection;
                    tempWaypoint.waypointableId = haulRoute.id;
                    tempWaypoint.waypointableType = "HaulRoute";

                    return createWaypointDirection(
                        tempWaypoint,
                        context.currentProjectInfo.claimed_by_team_subdomain,
                        context.currentProjectInfo.unique_token
                    ).then((response) => {
                        return haulRoute;
                    });
                }
                return haulRoute;
            })
            .catch((error) => {
                notification.error({
                    message: "Unable to create Haul Route",
                    description: error.message || "",
                });

                return null;
            });
    }
    if (currentMappable instanceof GateToDropoffRoute) {
        return PlotAPI.createGateToDropoffRoute(
            context.currentProjectInfo.claimed_by_team_subdomain || "projects",
            context.currentProjectInfo.unique_token,
            currentMappable,
            currentMapObject,
            context.currentProjectInfo.timezone
        )
            .then((res) => {
                if (!res) {
                    throw new Error("Unable to create Gate to Dropoff Route");
                }

                const gateToDropoffRoute = res as GateToDropoffRoute;

                if (gateToDropoffRoute && currentMappable.waypointDirection) {
                    const tempWaypoint = Object.create(currentMappable.waypointDirection) as WaypointDirection;
                    tempWaypoint.waypointableId = gateToDropoffRoute.id;
                    tempWaypoint.waypointableType = "GateToDropoffRoute";

                    return createWaypointDirection(
                        tempWaypoint,
                        context.currentProjectInfo.claimed_by_team_subdomain,
                        context.currentProjectInfo.unique_token
                    ).then((response) => {
                        return gateToDropoffRoute;
                    });
                }
                return gateToDropoffRoute;
            })
            .catch((error) => {
                notification.error({
                    message: "Unable to create Gate to Dropoff Route",
                    description: error.message || "",
                });

                return null;
            });
    }

    return Promise.resolve(null);
};

export const archiveMappable = async (
    mappable: Area | Dropoff | Equipment | Gate | HaulRoute | GateToDropoffRoute,
    context: GlobalStoreContextType
) => {
    if (mappable instanceof Area) {
        return PlotAPI.deleteArea(
            context.currentProjectInfo.claimed_by_team_subdomain || "projects",
            context.currentProjectInfo.unique_token,
            mappable
        ).then((data) => {
            if (!data) {
                notification.error({
                    message: "Unable to archive Area",
                    description: "The area could not be archived. Please try again.",
                });
            }
        });
    } else if (mappable instanceof Dropoff) {
        return PlotAPI.deleteDropoff(
            context.currentProjectInfo.claimed_by_team_subdomain || "projects",
            context.currentProjectInfo.unique_token,
            mappable
        ).then((data) => {
            if (!data) {
                notification.error({
                    message: "Unable to archive Dropoff",
                    description: "The dropoff could not be archived. Please try again.",
                });
            }
        });
    } else if (mappable instanceof Equipment) {
        return PlotAPI.deleteEquipment(
            context.currentProjectInfo.claimed_by_team_subdomain || "projects",
            context.currentProjectInfo.unique_token,
            mappable
        ).then((data) => {
            if (!data) {
                notification.error({
                    message: "Unable to archive Equipment",
                    description: "The equipment could not be archived. Please try again.",
                });
            }
        });
    } else if (mappable instanceof Gate) {
        return PlotAPI.deleteGate(
            context.currentProjectInfo.claimed_by_team_subdomain || "projects",
            context.currentProjectInfo.unique_token,
            mappable
        ).then((data) => {
            if (!data) {
                notification.error({
                    message: "Unable to archive Gate",
                    description: `Please check if a gate is already archived with the name ${mappable.name}`,
                });
            }
        });
    } else if (mappable instanceof HaulRoute) {
        return PlotAPI.deleteHaulRoute(
            context.currentProjectInfo.claimed_by_team_subdomain || "projects",
            context.currentProjectInfo.unique_token,
            mappable
        ).then((data) => {
            if (!data) {
                notification.error({
                    message: "Unable to archive Haul Route",
                    description: `Please check if a haul route is already archived with the name ${mappable.name}`,
                });
            }
        });
    } else {
        return PlotAPI.deleteGateToDropoffRoute(
            context.currentProjectInfo.claimed_by_team_subdomain || "projects",
            context.currentProjectInfo.unique_token,
            mappable
        ).then((data) => {
            if (!data) {
                notification.error({
                    message: "Unable to archive Gate to Dropoff Route",
                    description: `Please check if a route is already archived with the name ${mappable.name}`,
                });
            }
        });
    }
};

export const objectType = (objectType: geometry): string => {
    if (objectType === "lineString") {
        return "Line";
    } else if (objectType === "polygon") {
        return "Area";
    } else {
        return "Point";
    }
};

const mappableNameFor = (
    mappable: Area | Dropoff | Equipment | Gate | HaulRoute | GateToDropoffRoute
): string | null => {
    if (mappable instanceof Area) {
        return mappable.areaName;
    }
    if (mappable instanceof Equipment) {
        return mappable.equipmentName;
    }
    return mappable.name;
};

export const checkRequiredValues = (
    mappable: Area | Dropoff | Equipment | Gate | HaulRoute | GateToDropoffRoute,
    mapObject: MapObject,
    context: GlobalStoreContextType
) => {
    const name = mappableNameFor(mappable);

    if (!name || name.length == 0) {
        notification.error({
            message: "Cannot Save Map Location",
            description: "A name is required for your " + objectType(mapObject.geometry).toLowerCase(),
        });

        return false;
    }

    if (
        ["New Area", "New Line", "New Equipment", "New Point", "New Gate", "New Haul Route", "New Dropoff"].includes(
            name
        )
    ) {
        notification.error({
            message: "Cannot Save Map Location",
            description:
                "Please change your " + objectType(mapObject.geometry).toLowerCase() + " from the default name",
        });

        return false;
    }

    let mappableName = mappableNameFor(mappable);
    if (!mappableName) {
        notification.error({
            message: "Cannot Save Map Location",
            description: "A name is required for your " + objectType(mapObject.geometry).toLowerCase(),
        });
        return false;
    }
    mappableName = mappableName.toLowerCase().replaceAll(" ", "");
    const allMappables = [
        ...context.mapAreasForProject(context.currentProjectInfo.id),
        ...context.dropoffsForProject(context.currentProjectInfo.id),
        ...context.equipmentForProject(context.currentProjectInfo.id),
        ...context.gatesForProject(context.currentProjectInfo.id),
        ...context.haulRoutesForProject(context.currentProjectInfo.id),
    ];

    const duplicateName = allMappables
        // Ids are only unique per type, so the item being saved has to match on both.
        .filter((item) => !(item.id == mappable.id && item.constructor === mappable.constructor))
        .some((map) => {
            const name = mappableNameFor(map);
            return name?.toLowerCase().replaceAll(" ", "") == mappableName;
        });

    if (duplicateName) {
        notification.error({
            message: "Cannot Save Map Location",
            description: "A location with this name already exists",
        });
        return false;
    }

    return true;
};
